import React, { useState, useEffect } from "react";
import {
  BotStatus,
  apiService,
  GuildXPSettingsConfig,
  WelcomeSettingsConfig,
  TwitchChannel,
  BotInfo,
  Guild,
} from "../services/api";

const DEFAULT_WELCOME_GIF =
  "https://cdn.discordapp.com/attachments/1116776282160644106/1176282460959559753/trapezio.gif?ex=6a9e8ca8&is=6a9d3b28&hm=6ed5c4b78083b9c8d7fd2b7174e1424c0451653d73bdfad65eb2b4e0dfc28729&";

interface SettingsProps {
  botStatus: BotStatus | null;
  loading: boolean;
  selectedGuild?: string;
  guilds: Guild[];
  onSelectGuild?: (guildId: string) => void;
}

export const Settings: React.FC<SettingsProps> = ({
  botStatus,
  loading,
  selectedGuild,
  guilds,
  onSelectGuild,
}) => {
  const [activeTab, setActiveTab] = useState<
    "general" | "welcome" | "xp" | "twitch" | "fitgirl" | "logging"
  >("general");

  const [availableChannels, setAvailableChannels] = useState<{ id: string; name: string; type: string }[]>([]);

  useEffect(() => {
    if (selectedGuild) {
      apiService.getGuildChannels(selectedGuild).then((r) => {
        setAvailableChannels(r.channels.filter((c) => c.type === "text"));
      }).catch(() => setAvailableChannels([]));
    }
  }, [selectedGuild]);

  if (loading) {
    return <div className="loading">Caricamento impostazioni...</div>;
  }

  return (
    <div className="tab-content settings-page">
      <div className="settings-header-card">
        <div>
          <h2>Impostazioni Bot & Moduli</h2>
          <p className="subtitle">
            Configura benvenuto, livelli XP, notifiche, log e presenza di VortexBot
          </p>
        </div>

        {/* Guild selector header */}
        <div className="guild-picker">
          <label>Server attivo:</label>
          <select
            value={selectedGuild}
            onChange={(e) => onSelectGuild && onSelectGuild(e.target.value)}
            className="select-input"
          >
            {guilds.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Sub-Navigation Tabs */}
      <div className="settings-nav">
        <button
          className={`settings-nav-btn ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          🤖 Generale & Profilo
        </button>
        <button
          className={`settings-nav-btn ${activeTab === "welcome" ? "active" : ""}`}
          onClick={() => setActiveTab("welcome")}
        >
          👋 Benvenuto & GIF
        </button>
        <button
          className={`settings-nav-btn ${activeTab === "xp" ? "active" : ""}`}
          onClick={() => setActiveTab("xp")}
        >
          📊 XP & Livelli
        </button>
        <button
          className={`settings-nav-btn ${activeTab === "twitch" ? "active" : ""}`}
          onClick={() => setActiveTab("twitch")}
        >
          🔴 Twitch
        </button>
        <button
          className={`settings-nav-btn ${activeTab === "fitgirl" ? "active" : ""}`}
          onClick={() => setActiveTab("fitgirl")}
        >
          🎮 FitGirl Monitor
        </button>
        <button
          className={`settings-nav-btn ${activeTab === "logging" ? "active" : ""}`}
          onClick={() => setActiveTab("logging")}
        >
          📜 Canali Log
        </button>
      </div>

      {/* Settings Sub-panels */}
      <div className="settings-body">
        {activeTab === "general" && <GeneralSettingsTab botStatus={botStatus} />}
        {activeTab === "welcome" && selectedGuild && (
          <WelcomeSettingsTab guildId={selectedGuild} channels={availableChannels} />
        )}
        {activeTab === "xp" && selectedGuild && (
          <XPSettingsTab guildId={selectedGuild} channels={availableChannels} />
        )}
        {activeTab === "twitch" && selectedGuild && (
          <TwitchSettingsTab guildId={selectedGuild} channels={availableChannels} />
        )}
        {activeTab === "fitgirl" && (
          <FitgirlSettingsTab channels={availableChannels} />
        )}
        {activeTab === "logging" && (
          <LoggingSettingsTab channels={availableChannels} />
        )}
      </div>
    </div>
  );
};

/* ========================================================
   TAB 1: GENERAL & PROFILE & PRESENCE
   ======================================================== */
function GeneralSettingsTab({ botStatus }: { botStatus: BotStatus | null }) {
  const [botInfo, setBotInfo] = useState<BotInfo | null>(null);
  const [botName, setBotName] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [prefix, setPrefix] = useState("!");
  const [activityName, setActivityName] = useState("con i comandi");
  const [activityType, setActivityType] = useState<string>("PLAYING");
  const [presenceStatus, setPresenceStatus] = useState("online");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPresence, setSavingPresence] = useState(false);
  const [savingPrefix, setSavingPrefix] = useState(false);
  const [feedback, setFeedback] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    apiService.getBotInfo().then((info) => {
      setBotInfo(info);
      setBotName(info.botName);
    }).catch(() => {});

    apiService.getFullConfig().then((cfg) => {
      if (cfg.prefix) setPrefix(cfg.prefix);
    }).catch(() => {});
  }, []);

  const showFeedback = (msg: string, type: "success" | "error" = "success") => {
    setFeedback({ msg, type });
    setTimeout(() => setFeedback(null), 3000);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const formData = new FormData();
      if (botName) formData.append("newBotName", botName);
      if (avatarFile) formData.append("new-bot-avatar", avatarFile);
      if (bannerFile) formData.append("new-bot-banner", bannerFile);

      const res = await apiService.updateBotProfile(formData);
      if (res.success) {
        showFeedback("Profilo bot aggiornato con successo!");
        const updated = await apiService.getBotInfo();
        setBotInfo(updated);
        setAvatarFile(null);
        setBannerFile(null);
      } else {
        showFeedback(res.message || "Errore aggiornamento", "error");
      }
    } catch {
      showFeedback("Errore di rete durante l'aggiornamento", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleUpdatePresence = async () => {
    setSavingPresence(true);
    try {
      const res = await apiService.setBotActivity({
        status: presenceStatus,
        activityName,
        activityType,
      });
      if (res.success) {
        showFeedback("Stato bot aggiornato in tempo reale!");
      }
    } catch {
      showFeedback("Errore aggiornamento attività", "error");
    } finally {
      setSavingPresence(false);
    }
  };

  const handleUpdatePrefix = async () => {
    setSavingPrefix(true);
    try {
      const res = await apiService.updateFullConfig({ prefix });
      if (res.success) {
        showFeedback("Prefix comandi salvato!");
      }
    } catch {
      showFeedback("Errore salvataggio prefix", "error");
    } finally {
      setSavingPrefix(false);
    }
  };

  return (
    <div className="tab-pane">
      {feedback && (
        <div className={`toast-notification ${feedback.type}`}>
          {feedback.msg}
        </div>
      )}

      {/* Bot Info Header */}
      <div className="card profile-card">
        <div
          className="profile-banner"
          style={{ backgroundImage: botInfo?.botBanner ? `url(${botInfo.botBanner})` : "none" }}
        >
          <img
            src={botInfo?.botAvatar || "/default-avatar.png"}
            alt="Bot Avatar"
            className="bot-avatar-large"
          />
        </div>
        <div className="profile-details">
          <div className="bot-title">
            <h3>{botInfo?.botName || "VortexBot"}</h3>
            {botInfo?.isVerified && <span className="verified-badge">✓ Verificato</span>}
            <span className={`status-pill ${botStatus?.online ? "online" : "offline"}`}>
              {botStatus?.online ? "Online" : "Offline"}
            </span>
          </div>
          <p className="bot-id">ID Bot: <code>{botInfo?.botId || "N/A"}</code></p>
        </div>
      </div>

      <div className="grid-2-col">
        {/* Profile Edit Form */}
        <div className="card">
          <h3>🎨 Personalizzazione Bot</h3>
          <p className="card-desc">Cambia il nome, avatar e banner del bot Discord</p>

          <form onSubmit={handleUpdateProfile} className="form-stack">
            <div className="form-group">
              <label>Nome Bot</label>
              <input
                type="text"
                value={botName}
                onChange={(e) => setBotName(e.target.value)}
                className="text-input"
              />
            </div>

            <div className="form-group">
              <label>Nuovo Avatar (PNG, JPG, GIF)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
                className="file-input"
              />
            </div>

            <div className="form-group">
              <label>Nuovo Banner (PNG, JPG)</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setBannerFile(e.target.files?.[0] || null)}
                className="file-input"
              />
            </div>

            <button type="submit" disabled={savingProfile} className="btn primary-btn">
              {savingProfile ? "Salvataggio..." : "💾 Salva Profilo Bot"}
            </button>
          </form>
        </div>

        {/* Presence & Status */}
        <div className="card">
          <h3>⚡ Stato & Attività in Tempo Reale</h3>
          <p className="card-desc">Imposta l'attività mostrata nel profilo Discord del bot</p>

          <div className="form-stack">
            <div className="form-group">
              <label>Stato Online</label>
              <select
                value={presenceStatus}
                onChange={(e) => setPresenceStatus(e.target.value)}
                className="select-input"
              >
                <option value="online">🟢 Online</option>
                <option value="idle">🌙 Inattivo (Idle)</option>
                <option value="dnd">🔴 Non disturbare (DND)</option>
                <option value="invisible">⚪ Invisibile</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tipo Attività</label>
              <select
                value={activityType}
                onChange={(e) => setActivityType(e.target.value)}
                className="select-input"
              >
                <option value="PLAYING">Sta giocando a (Playing)</option>
                <option value="STREAMING">In diretta su (Streaming)</option>
                <option value="LISTENING">Ascolta (Listening)</option>
                <option value="WATCHING">Guarda (Watching)</option>
                <option value="COMPETING">Gareggia in (Competing)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Testo Attività</label>
              <input
                type="text"
                value={activityName}
                onChange={(e) => setActivityName(e.target.value)}
                placeholder="es. musica con /play"
                className="text-input"
              />
            </div>

            <button onClick={handleUpdatePresence} disabled={savingPresence} className="btn secondary-btn">
              {savingPresence ? "Aggiornamento..." : "🚀 Applica Attività Subito"}
            </button>
          </div>

          <hr className="divider" />

          <h3>⚙️ Prefix Comandi Standard</h3>
          <div className="form-inline">
            <input
              type="text"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              maxLength={5}
              style={{ width: "80px" }}
              className="text-input"
            />
            <button onClick={handleUpdatePrefix} disabled={savingPrefix} className="btn primary-btn">
              {savingPrefix ? "Salvataggio..." : "Salva Prefix"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========================================================
   TAB 2: WELCOME SYSTEM & GIF (MODERNIZED)
   ======================================================== */
function WelcomeSettingsTab({
  guildId,
  channels,
}: {
  guildId: string;
  channels: { id: string; name: string; type: string }[];
}) {
  const [settings, setSettings] = useState<WelcomeSettingsConfig>({
    guildId,
    welcomeEnabled: false,
    welcomeChannelId: "",
    messageTemplate: "Benvenuto {user} nel server **{guild}**!",
    roleId: "",
    embedColor: "#5865F2",
    embedImage: DEFAULT_WELCOME_GIF,
    fieldLabel: "📜 Regole & Info",
    fieldValue: "Leggi il regolamento e divertiti nella community!",
  });
  const [roles, setRoles] = useState<{ id: string; name: string; color: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testFeedback, setTestFeedback] = useState<string | null>(null);

  useEffect(() => {
    apiService.getWelcomeSettings(guildId).then((s) => {
      setSettings({
        ...s,
        embedImage: s.embedImage || DEFAULT_WELCOME_GIF,
      });
    }).catch(() => {});

    apiService.getGuildChannels(guildId).then((r) => {
      setRoles(r.roles);
    }).catch(() => {});
  }, [guildId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateWelcomeSettings(guildId, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Errore salvataggio benvenuto");
    } finally {
      setSaving(false);
    }
  };

  const handleTestWelcome = async () => {
    if (!settings.welcomeChannelId) {
      alert("Seleziona prima un canale di benvenuto nel menu a tendina!");
      return;
    }
    try {
      setTesting(true);
      const res = await apiService.testWelcomeMessage(guildId, settings);
      if (res.success) {
        setTestFeedback(res.message || "Messaggio di test inviato! Auto-delete tra 30s.");
        setTimeout(() => setTestFeedback(null), 6000);
      } else {
        alert("Errore invio test: " + (res.message || "Errore sconosciuto"));
      }
    } catch (err: any) {
      alert("Errore invio test: " + (err.response?.data?.error || err.message));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="tab-pane">
      <div className="grid-2-col">
        {/* Settings Column */}
        <div className="card">
          <h3>👋 Configurazione Messaggio di Benvenuto</h3>
          <p className="card-desc">
            Personalizza il messaggio, embed, colore, GIF animata e ruolo automatico
          </p>

          <div className="switch-row">
            <label className="switch">
              <input
                type="checkbox"
                checked={settings.welcomeEnabled}
                onChange={(e) => setSettings({ ...settings, welcomeEnabled: e.target.checked })}
              />
              <span className="slider round"></span>
            </label>
            <span className="switch-label">Abilita messaggio di benvenuto</span>
          </div>

          <div className="form-group">
            <label>Canale di Invio Benvenuto</label>
            <select
              value={settings.welcomeChannelId}
              onChange={(e) => setSettings({ ...settings, welcomeChannelId: e.target.value })}
              className="select-input"
            >
              <option value="">— Seleziona Canale —</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Ruolo Automatico (Auto-Role)</label>
            <select
              value={settings.roleId}
              onChange={(e) => setSettings({ ...settings, roleId: e.target.value })}
              className="select-input"
            >
              <option value="">— Nessun Ruolo Automatico —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  @{r.name}
                </option>
              ))}
            </select>
            <small className="help-text">Assegnato immediatamente al momento dell'ingresso</small>
          </div>

          <div className="form-group">
            <label>Messaggio Principale</label>
            <textarea
              value={settings.messageTemplate}
              onChange={(e) => setSettings({ ...settings, messageTemplate: e.target.value })}
              rows={3}
              className="text-input"
              placeholder="Benvenuto {user} nel server {guild}!"
            />
            <small className="help-text">
              Tag: <code>{'{user}'}</code> (menzione), <code>{'{username}'}</code> (nome), <code>{'{guild}'}</code> (server), <code>{'{memberCount}'}</code> (totale)
            </small>
          </div>

          <div className="form-group">
            <label>Colore Embed Discord</label>
            <div className="color-picker-row">
              <input
                type="color"
                value={settings.embedColor || "#5865F2"}
                onChange={(e) => setSettings({ ...settings, embedColor: e.target.value })}
                className="color-input"
              />
              <span>{settings.embedColor}</span>
              <button
                type="button"
                className="btn secondary-btn small-btn"
                onClick={() => setSettings({ ...settings, embedColor: "#5865F2" })}
              >
                Reset Blurple
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>URL Immagine o GIF Banner</label>
            <input
              type="text"
              value={settings.embedImage}
              onChange={(e) => setSettings({ ...settings, embedImage: e.target.value })}
              className="text-input"
              placeholder="https://..."
            />
            <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
              <button
                type="button"
                className="btn secondary-btn small-btn"
                onClick={() => setSettings({ ...settings, embedImage: DEFAULT_WELCOME_GIF })}
              >
                Usa GIF Trapezio Predefinita
              </button>
            </div>
          </div>

          <div className="grid-2-col">
            <div className="form-group">
              <label>Titolo Campo Info</label>
              <input
                type="text"
                value={settings.fieldLabel}
                onChange={(e) => setSettings({ ...settings, fieldLabel: e.target.value })}
                className="text-input"
              />
            </div>

            <div className="form-group">
              <label>Contenuto Campo Info</label>
              <input
                type="text"
                value={settings.fieldValue}
                onChange={(e) => setSettings({ ...settings, fieldValue: e.target.value })}
                className="text-input"
              />
            </div>
          </div>

          {testFeedback && (
            <div className="toast-notification success" style={{ marginTop: 12 }}>
              {testFeedback}
            </div>
          )}

          <div className="action-row" style={{ display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={handleTestWelcome}
              disabled={testing || !settings.welcomeChannelId}
              className="btn secondary-btn"
              title="Invia subito un messaggio di benvenuto di prova che si auto-cancellerà dopo 30 secondi"
            >
              {testing ? "Invio in corso..." : "🧪 Invia Prova nel Canale (Auto-delete 30s)"}
            </button>
            <button onClick={handleSave} disabled={saving} className="btn primary-btn">
              {saving ? "Salvataggio..." : saved ? "✓ Salvato!" : "💾 Salva Configurazione"}
            </button>
          </div>
        </div>

        {/* Live Preview Column */}
        <div className="card welcome-preview-card">
          <h3>👁️ Anteprima Live Discord</h3>
          <p className="card-desc">Così apparirà il messaggio inviato nel canale quando entra un utente</p>

          <div className="discord-message-mock">
            <div className="discord-msg-header">
              <span className="discord-msg-author">VortexBot</span>
              <span className="bot-tag">BOT</span>
              <span className="discord-msg-time">Oggi alle 12:00</span>
            </div>
            <div className="discord-msg-text">
              👋 Un caloroso benvenuto a <span className="mention-pill">@NuovoUtente</span>!
            </div>

            {/* Embed Box */}
            <div
              className="discord-embed-box"
              style={{ borderLeftColor: settings.embedColor || "#5865F2" }}
            >
              <div className="embed-author-row">
                <img
                  src="https://cdn.discordapp.com/embed/avatars/0.png"
                  alt="Avatar"
                  className="embed-author-icon"
                />
                <span className="embed-author-name">Nuovo membro entrato: NuovoUtente</span>
              </div>

              <h4 className="embed-title">🎉 Benvenuto nel server!</h4>

              <div className="embed-desc">
                {settings.messageTemplate
                  .replace(/{user}/g, "@NuovoUtente")
                  .replace(/{username}/g, "NuovoUtente")
                  .replace(/{guild}/g, "Il Tuo Server")
                  .replace(/{memberCount}/g, "150")}
              </div>

              <div className="embed-fields-grid">
                <div className="embed-field">
                  <span className="field-name">👥 Membro N°</span>
                  <span className="field-value"><code>#150</code></span>
                </div>
                <div className="embed-field">
                  <span className="field-name">📅 Account Creato</span>
                  <span className="field-value">1 anno fa</span>
                </div>
              </div>

              {settings.fieldLabel && settings.fieldValue && (
                <div className="embed-field full-width">
                  <span className="field-name">{settings.fieldLabel}</span>
                  <span className="field-value">{settings.fieldValue}</span>
                </div>
              )}

              {/* GIF Banner */}
              {settings.embedImage && (
                <div className="embed-image-wrap">
                  <img
                    src={settings.embedImage}
                    alt="Welcome Banner GIF"
                    className="embed-banner-img"
                    onError={(e: any) => {
                      e.target.src = DEFAULT_WELCOME_GIF;
                    }}
                  />
                </div>
              )}

              <div className="embed-footer">
                <span>VortexBot • ID: 123456789 • Benvenuto nel server!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========================================================
   TAB 3: XP & LEVELING SYSTEM
   ======================================================== */
function XPSettingsTab({
  guildId,
  channels,
}: {
  guildId: string;
  channels: { id: string; name: string; type: string }[];
}) {
  const [settings, setSettings] = useState<GuildXPSettingsConfig>({
    enabled: true,
    xpPerMessage: 5,
    cooldownSec: 5,
    levelUpMessages: true,
    levelUpChannelId: "",
    roleRewards: [],
  });
  const [roles, setRoles] = useState<{ id: string; name: string; color: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiService.getXPSettings(guildId).then((s) => setSettings(s)).catch(() => {});
    apiService.getGuildChannels(guildId).then((r) => {
      setRoles(r.roles);
    }).catch(() => {});
  }, [guildId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateXPSettings(guildId, settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Errore salvataggio impostazioni XP");
    } finally {
      setSaving(false);
    }
  };

  const addRoleReward = () => {
    setSettings((prev) => ({
      ...prev,
      roleRewards: [...prev.roleRewards, { level: 5, roleId: "" }],
    }));
  };

  const removeRoleReward = (idx: number) => {
    setSettings((prev) => ({
      ...prev,
      roleRewards: prev.roleRewards.filter((_, i) => i !== idx),
    }));
  };

  const updateRoleReward = (idx: number, key: "level" | "roleId", val: string | number) => {
    setSettings((prev) => {
      const copy = [...prev.roleRewards];
      copy[idx] = { ...copy[idx], [key]: val };
      return { ...prev, roleRewards: copy };
    });
  };

  return (
    <div className="tab-pane">
      <div className="card">
        <h3>⚙️ Configurazione Sistema Punti & Livelli (XP)</h3>
        <p className="card-desc">
          Gestisci l'accumulo di esperienza degli utenti nei messaggi e le notifiche di avanzamento livello
        </p>

        <div className="switch-row">
          <label className="switch">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            />
            <span className="slider round"></span>
          </label>
          <span className="switch-label">Abilita guadagno XP nei canali</span>
        </div>

        <div className="grid-2-col">
          <div className="form-group">
            <label>XP Guadagnati per Messaggio</label>
            <input
              type="number"
              min={1}
              max={100}
              value={settings.xpPerMessage}
              onChange={(e) => setSettings({ ...settings, xpPerMessage: parseInt(e.target.value) || 5 })}
              className="text-input"
            />
          </div>

          <div className="form-group">
            <label>Cooldown XP (Secondi)</label>
            <input
              type="number"
              min={1}
              max={300}
              value={settings.cooldownSec}
              onChange={(e) => setSettings({ ...settings, cooldownSec: parseInt(e.target.value) || 5 })}
              className="text-input"
            />
          </div>
        </div>

        <hr className="divider" />

        <h4>📢 Notifiche di Level-Up</h4>
        <div className="switch-row">
          <label className="switch">
            <input
              type="checkbox"
              checked={settings.levelUpMessages}
              onChange={(e) => setSettings({ ...settings, levelUpMessages: e.target.checked })}
            />
            <span className="slider round"></span>
          </label>
          <span className="switch-label">Invia messaggio di congratulazioni al level-up</span>
        </div>

        <div className="form-group">
          <label>Canale Notifiche Level-Up</label>
          <select
            value={settings.levelUpChannelId}
            onChange={(e) => setSettings({ ...settings, levelUpChannelId: e.target.value })}
            className="select-input"
          >
            <option value="">💬 Stesso canale dove l'utente scrive (Default)</option>
            {channels.map((ch) => (
              <option key={ch.id} value={ch.id}>
                #{ch.name} (Canale dedicato)
              </option>
            ))}
          </select>
        </div>

        <hr className="divider" />

        <h4>🎁 Role Rewards (Ruoli Sbloccabili con i Livelli)</h4>
        <div className="rewards-list">
          {settings.roleRewards.map((reward, idx) => (
            <div key={idx} className="reward-row">
              <span className="reward-tag">Livello:</span>
              <input
                type="number"
                min={1}
                value={reward.level}
                onChange={(e) => updateRoleReward(idx, "level", parseInt(e.target.value) || 1)}
                className="text-input num-input"
              />
              <span>→ Ruolo:</span>
              <select
                value={reward.roleId}
                onChange={(e) => updateRoleReward(idx, "roleId", e.target.value)}
                className="select-input flex-1"
              >
                <option value="">— Seleziona Ruolo —</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>
                    @{r.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => removeRoleReward(idx)}
                className="btn danger-icon-btn"
                title="Rimuovi premio"
              >
                ✕
              </button>
            </div>
          ))}

          <button onClick={addRoleReward} className="btn secondary-btn add-btn">
            + Aggiungi Nuovo Ruolo Premio
          </button>
        </div>

        <div className="action-row">
          <button onClick={handleSave} disabled={saving} className="btn primary-btn large-btn">
            {saving ? "Salvataggio..." : saved ? "✓ Salvato!" : "💾 Salva Configurazione XP"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================================================
   TAB 4: TWITCH NOTIFICATIONS
   ======================================================== */
function TwitchSettingsTab({
  guildId,
  channels,
}: {
  guildId: string;
  channels: { id: string; name: string; type: string }[];
}) {
  const [streamers, setStreamers] = useState<TwitchChannel[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiService.getTwitchChannels(guildId).then((r) => setStreamers(r.monitoredChannels)).catch(() => {});
  }, [guildId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateTwitchChannels(guildId, streamers);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Errore salvataggio Twitch");
    } finally {
      setSaving(false);
    }
  };

  const addStreamer = () => {
    setStreamers((prev) => [...prev, { twitchName: "", discordChannelId: "" }]);
  };

  const removeStreamer = (idx: number) => {
    setStreamers((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateStreamer = (idx: number, key: "twitchName" | "discordChannelId", val: string) => {
    setStreamers((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: val };
      return copy;
    });
  };

  return (
    <div className="tab-pane">
      <div className="card">
        <h3>🔴 Monitoraggio Streamer Twitch</h3>
        <p className="card-desc">
          Notifiche automatiche nei canali Discord scelti quando uno streamer va in diretta
        </p>

        <div className="twitch-channels-list">
          {streamers.map((ch, idx) => (
            <div key={idx} className="twitch-row">
              <input
                type="text"
                placeholder="Nome canale Twitch (es. ninjav)"
                value={ch.twitchName}
                onChange={(e) => updateStreamer(idx, "twitchName", e.target.value)}
                className="text-input flex-1"
              />
              <span>→ Notifica in:</span>
              <select
                value={ch.discordChannelId}
                onChange={(e) => updateStreamer(idx, "discordChannelId", e.target.value)}
                className="select-input flex-1"
              >
                <option value="">— Seleziona Canale —</option>
                {channels.map((gc) => (
                  <option key={gc.id} value={gc.id}>
                    #{gc.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => removeStreamer(idx)}
                className="btn danger-icon-btn"
                title="Rimuovi canale"
              >
                ✕
              </button>
            </div>
          ))}

          <button onClick={addStreamer} className="btn twitch-btn add-btn">
            + Aggiungi Streamer Twitch
          </button>
        </div>

        <div className="action-row">
          <button onClick={handleSave} disabled={saving} className="btn twitch-btn large-btn">
            {saving ? "Salvataggio..." : saved ? "✓ Salvato!" : "💾 Salva Notifiche Twitch"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ========================================================
   TAB 5: FITGIRL REPACKS MONITOR (WITH CHANNEL SELECTOR)
   ======================================================== */
function FitgirlSettingsTab({
  channels,
}: {
  channels: { id: string; name: string; type: string }[];
}) {
  const [config, setConfig] = useState({
    channelId: "",
    checkInterval: "0 */2 * * *",
    rssUrl: "https://fitgirl-repacks.site/feed/",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiService.getFullConfig().then((cfg) => {
      if (cfg.fitgirlMonitor) {
        setConfig({
          channelId: cfg.fitgirlMonitor.channelId || "",
          checkInterval: cfg.fitgirlMonitor.checkInterval || "0 */2 * * *",
          rssUrl: cfg.fitgirlMonitor.rssUrl || "https://fitgirl-repacks.site/feed/",
        });
      }
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateFullConfig({ fitgirlMonitor: config });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Errore salvataggio impostazioni FitGirl");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-pane">
      <div className="card">
        <h3>🎮 Monitor Giochi & Repacks (FitGirl)</h3>
        <p className="card-desc">
          Controlla periodicamente i feed RSS e invia un annuncio con copertina e info
        </p>

        <div className="form-stack">
          <div className="form-group">
            <label>Canale Discord per Annunci Nuovi Giochi</label>
            <select
              value={config.channelId}
              onChange={(e) => setConfig({ ...config, channelId: e.target.value })}
              className="select-input"
            >
              <option value="">— Seleziona Canale —</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name} (ID: {ch.id})
                </option>
              ))}
            </select>
            <small className="help-text">Seleziona il canale dove il bot pubblicherà gli annunci dei nuovi giochi</small>
          </div>

          <div className="form-group">
            <label>URL Feed RSS</label>
            <input
              type="text"
              value={config.rssUrl}
              onChange={(e) => setConfig({ ...config, rssUrl: e.target.value })}
              className="text-input"
            />
          </div>

          <div className="form-group">
            <label>Intervallo Controllo (Espressione Cron)</label>
            <input
              type="text"
              value={config.checkInterval}
              onChange={(e) => setConfig({ ...config, checkInterval: e.target.value })}
              className="text-input"
            />
            <small className="help-text">Default: <code>0 */2 * * *</code> (ogni 2 ore)</small>
          </div>

          <div className="action-row">
            <button onClick={handleSave} disabled={saving} className="btn primary-btn large-btn">
              {saving ? "Salvataggio..." : saved ? "✓ Salvato!" : "💾 Salva Configurazione FitGirl"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ========================================================
   TAB 6: LOGGING CHANNELS (WITH CHANNEL SELECTORS)
   ======================================================== */
function LoggingSettingsTab({
  channels,
}: {
  channels: { id: string; name: string; type: string }[];
}) {
  const [logging, setLogging] = useState({
    guildJoinLogsId: "",
    guildLeaveLogsId: "",
    commandLogsChannelId: "",
    errorLogs: "",
  });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiService.getFullConfig().then((cfg) => {
      if (cfg.logging) {
        setLogging({
          guildJoinLogsId: cfg.logging.guildJoinLogsId || "",
          guildLeaveLogsId: cfg.logging.guildLeaveLogsId || "",
          commandLogsChannelId: cfg.logging.commandLogsChannelId || "",
          errorLogs: cfg.logging.errorLogs || "",
        });
      }
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiService.updateFullConfig({ logging });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {
      alert("Errore salvataggio canali log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tab-pane">
      <div className="card">
        <h3>📜 Canali di Audit & Logging</h3>
        <p className="card-desc">
          Seleziona i canali dedicati per ciascun tipo di evento del bot
        </p>

        <div className="grid-2-col">
          <div className="form-group">
            <label>Canale Log Ingressi (Guild Join)</label>
            <select
              value={logging.guildJoinLogsId}
              onChange={(e) => setLogging({ ...logging, guildJoinLogsId: e.target.value })}
              className="select-input"
            >
              <option value="">— Nessun Canale Selezionato —</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name} (ID: {ch.id})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Canale Log Uscite (Guild Leave)</label>
            <select
              value={logging.guildLeaveLogsId}
              onChange={(e) => setLogging({ ...logging, guildLeaveLogsId: e.target.value })}
              className="select-input"
            >
              <option value="">— Nessun Canale Selezionato —</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name} (ID: {ch.id})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Canale Log Esecuzione Comandi</label>
            <select
              value={logging.commandLogsChannelId}
              onChange={(e) => setLogging({ ...logging, commandLogsChannelId: e.target.value })}
              className="select-input"
            >
              <option value="">— Nessun Canale Selezionato —</option>
              {channels.map((ch) => (
                <option key={ch.id} value={ch.id}>
                  #{ch.name} (ID: {ch.id})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Webhook URL Discord per Errori Bot</label>
            <input
              type="text"
              value={logging.errorLogs}
              onChange={(e) => setLogging({ ...logging, errorLogs: e.target.value })}
              className="text-input"
              placeholder="https://discord.com/api/webhooks/..."
            />
          </div>
        </div>

        <div className="action-row">
          <button onClick={handleSave} disabled={saving} className="btn primary-btn large-btn">
            {saving ? "Salvataggio..." : saved ? "✓ Salvato!" : "💾 Salva Canali Log"}
          </button>
        </div>
      </div>
    </div>
  );
}
